import { z } from 'zod';
import { macroVisualDigestSchema, readGrantSchema } from '../macroCalibration';
import { playbackBindingSchema } from '../macroExecution';
import { macroRegionSchema, macroTreeGraphSchema } from '../macroTree';

const boundedId = z.string().min(1).max(128);
const endpoint = z.string().url().max(2048);

/** A short-lived object store exposed only by the local Host loopback API. */
export const localVisualTestWorkspaceSchema = z.object({
  workspaceId: z.string().uuid(),
  scopeId: boundedId,
  uploadEndpoint: endpoint,
  compileEndpoint: endpoint,
  deleteEndpoint: endpoint,
  assetGrantEndpoint: endpoint,
  assetToken: z.string().min(1).max(4096),
  expiresAtUnixMs: z.number().int().positive(),
}).strict();

export const localVisualTestFrameSchema = z.object({
  id: boundedId,
  offsetMs: z.number().int().min(-250).max(250),
  capturedAtMs: z.number().int().nonnegative().safe(),
  pixelDigest: macroVisualDigestSchema,
  asset: readGrantSchema,
}).strict();

export const localVisualTestSampleSchema = z.object({
  nodeId: boundedId,
  anchorMs: z.number().int().nonnegative().safe(),
  region: macroRegionSchema,
  width: z.number().int().positive().max(16_384),
  height: z.number().int().positive().max(16_384),
  matchThresholdPercent: z.number().int().min(0).max(100),
  frames: z.array(localVisualTestFrameSchema).min(3).max(32),
}).strict().superRefine((sample, context) => {
  if (sample.width * sample.height > 16_777_216) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Capture exceeds 16 megapixels' });
  }
  if (sample.region.x + sample.region.width > sample.width
    || sample.region.y + sample.region.height > sample.height) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['region'], message: 'Region is outside the captured frame' });
  }
  if (sample.frames.filter((frame) => frame.offsetMs === 0).length !== 1) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['frames'], message: 'Temporal neighborhood requires exactly one anchor frame' });
  }
});

export const localVisualTestCompileRequestSchema = z.object({
  samples: z.array(localVisualTestSampleSchema).min(1),
}).strict();

/** Manifest and graph generated inside the local Host; no campaign record exists. */
export const localVisualTestCompiledSchema = z.object({
  testId: z.string().uuid(),
  scopeId: boundedId,
  revision: macroVisualDigestSchema,
  manifest: readGrantSchema,
  manifestDigest: macroVisualDigestSchema,
  workPlanDigest: macroVisualDigestSchema,
  expectedBindings: z.number().int().positive().max(16_384),
  expectedComparisons: z.number().int().positive().max(1_048_576),
  tree: macroTreeGraphSchema,
  bindings: z.array(playbackBindingSchema).min(1).max(16_384),
}).strict();

export const localVisualTestPrepareInputSchema = localVisualTestCompiledSchema.extend({
  assetGrantEndpoint: endpoint,
  assetToken: z.string().min(1).max(4096),
  expiresAtUnixMs: z.number().int().positive(),
}).strict();

export type LocalVisualTestWorkspace = z.infer<typeof localVisualTestWorkspaceSchema>;
export type LocalVisualTestFrame = z.infer<typeof localVisualTestFrameSchema>;
export type LocalVisualTestSample = z.infer<typeof localVisualTestSampleSchema>;
export type LocalVisualTestCompileRequest = z.infer<typeof localVisualTestCompileRequestSchema>;
export type LocalVisualTestCompiled = z.infer<typeof localVisualTestCompiledSchema>;
export type LocalVisualTestPrepareInput = z.infer<typeof localVisualTestPrepareInputSchema>;
